import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// #247 — the Map | Street View switch box, moved out of the event popup unchanged so the
// camera modal shows the same control.

export function ViewSwitch({
  view,
  checking,
  disabled,
  onMap,
  onStreetView,
  className,
}: {
  view: 'map' | 'street'
  checking: boolean
  disabled: boolean
  onMap: () => void
  onStreetView: () => void
  className?: string
}) {
  const { t } = useTranslation('events')
  return (
    <div
      role="group"
      aria-label={t('locationMap.viewSwitch')}
      className={cn(
        'absolute left-2 top-2 z-10 flex rounded-lg border border-border bg-background/95 p-0.5 shadow-md backdrop-blur-sm',
        className,
      )}
    >
      <ViewButton pressed={view === 'map'} onClick={onMap}>
        {t('locationMap.map')}
      </ViewButton>
      <ViewButton pressed={view === 'street'} busy={checking} disabled={disabled} onClick={onStreetView}>
        {checking && <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden />}
        {t('streetView.title')}
      </ViewButton>
    </div>
  )
}

function ViewButton({
  pressed,
  busy = false,
  disabled = false,
  onClick,
  children,
}: {
  pressed: boolean
  busy?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-busy={busy}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-9 wide:h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-60',
        pressed
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
